import { ButtonHeader } from '../../../Header/Style';
import SVG from '../../../SVG';
import { Control, ControlContainer } from '../../../Controls/styled';
import {
  getFilterColumn,
  getSortColumn,
  setFilterColumn,
  setSortColumn,
} from '../../../../store/utils';
import Table from '../../../Table';
import useExportsController from '../useExportsController';
import { useAbility } from '../../../Hooks/useAbility';

const useCompaniesController = () => {
  const {
    getInvoice,
    setInvoice,
    validateForm,
    setModal,
    getFormMessage,
    currentRoute,
    enqueueSnackbar,
    deleteCompanies: deleteCompaniesAction,
    autocompleteCompany,
    filterCompany,
    createCompany,
    updateCompany,
  } = useExportsController();
  const { Can } = useAbility();

  const companies = getInvoice('filters.companies.data.data');

  const checkCompanyIds = getInvoice('selecteds.companies');

  const setCheckCompanyIds = (selectedCompanyIds: number[]) =>
    setInvoice('selecteds.companies', selectedCompanyIds);

  const allCompanyChecks = companies.every(({ id }) =>
    checkCompanyIds.includes(id),
  );

  const companyCheckAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked;
    if (checked) {
      setCheckCompanyIds(companies.map(({ id }) => id));
    } else {
      setCheckCompanyIds([]);
    }
  };

  const checkCompanyId = (companyId: number) => {
    const newcheckIds = checkCompanyIds.includes(companyId)
      ? checkCompanyIds.filter((id) => id !== companyId)
      : [...checkCompanyIds, companyId];
    setCheckCompanyIds(newcheckIds);
  };

  const deleteCompanyLoadings = getInvoice('loadings.deleteCompanies');

  const setDeleteCompanyLoadings = (setDeleteCompanyLoadings: boolean[]) =>
    setInvoice('loadings.deleteCompanies', setDeleteCompanyLoadings);

  const deleteCompanies = (companyIds: number[] = checkCompanyIds) =>
    new Promise((resolve, reject) => {
      const length = companyIds.length;

      const indexes = companyIds.map((companyId) =>
        companies.findIndex(({ id }) => companyId === id),
      );

      setDeleteCompanyLoadings(
        companies.map((_, i) =>
          indexes.includes(i) ? true : deleteCompanyLoadings[i],
        ),
      );

      deleteCompaniesAction(companyIds)
        .then(() => {
          const newCheckIds = checkCompanyIds.filter(
            (id) => !companyIds.includes(id),
          );
          setCheckCompanyIds(newCheckIds);
          filterCompany();
          resolve(true);
          enqueueSnackbar(
            length === 1
              ? 'A companhia foi excluída com sucesso'
              : 'As companhias foram excluídas com sucesso',
            {
              variant: 'success',
            },
          );
        })
        .catch(() => {
          reject(false);
          enqueueSnackbar(
            length === 1
              ? 'Houve uma falha ao excluir a companhia'
              : 'Houve uma falha ao excluir as companhias',
            {
              variant: 'error',
            },
          );
        })
        .finally(() => {
          setDeleteCompanyLoadings(
            companies.map((_, i) =>
              indexes.includes(i) ? false : deleteCompanyLoadings[i],
            ),
          );
        });
    });

  const editCompany = ({ id, name }: Company) => {
    setModal('single.updateCompany', true);
    setInvoice('forms.companies.update', { id, name });
  };

  const searchFilter = getInvoice('filters.companies.filter');
  const setSearchFilter = (filter: SearchFilter) =>
    setInvoice('filters.companies.filter', filter);

  const getFilterField = (field: string, operator?: OPERATOR) =>
    getFilterColumn(field, searchFilter, operator);

  const setFilterField = (field: string, operator?: OPERATOR) => (e) => {
    let filter = setFilterColumn(field, e.target.value, searchFilter, operator);
    setSearchFilter({
      ...filter,
      page: 1,
    });
  };

  const getSortField = (field: string) => getSortColumn(field, searchFilter);

  const setSortField = (field: string) => (value) =>
    setSearchFilter(setSortColumn(field, value, searchFilter));

  const getAutocompleteItems = <
    T extends keyof InvoiceState['autocompletes']['companies'],
  >(
    field: T,
  ) =>
    getInvoice(`autocompletes.companies.${field}.data`).map((name, id) => ({
      id,
      name,
    }));

  const setAutocompleteItems = (
    field: keyof InvoiceState['autocompletes']['companies'],
  ) => {
    return autocompleteCompany(field);
  };

  const setAutocompleteFilterField =
    (field: keyof InvoiceState['autocompletes']['companies']) => (e) => {
      setInvoice(
        `autocompletes.companies.${field}.filter.value`,
        e.target.value,
      );
    };

  const setField = (field: any, operator?: OPERATOR) => (e) => {
    setFilterField(field, operator)(e);
    setAutocompleteFilterField(field)(e);
  };

  const onLineClickHandler = (index: number) => (e) => {
    var senderElementName = e.target.tagName.toLowerCase();
    if (['input', 'image', 'svg', 'path', 'button'].includes(senderElementName))
      return;
    const companyId = companies[index].id;
    checkCompanyId(companyId);
  };

  const hasCheckedCompanies = checkCompanyIds.length > 0;
  const hasFilters = (searchFilter.filter?.length ?? 0) > 0;
  const clearAll = () => {
    setInvoice('filters.companies.filter', {
      ...getInvoice('filters.companies.filter'),
      filter: [],
      orderBy: [],
      page: 1,
    });
    setInvoice('selecteds.companies', []);
  };
  const CompanyControls = () => {
    return currentRoute.includes('/tools/exports/companies') ? (
      <>
        <Can I="create" a="Company">
          {() => (
            <ButtonHeader
              className="button-header"
              onClick={() => {
                setModal('single.createCompany', true);
              }}
            >
              <div
                style={{
                  marginRight: '20px',
                }}
              >
                <SVG.Icon.Plus fill="#364856" />
              </div>
              NOVA COMPANHIA
            </ButtonHeader>
          )}
        </Can>
        <ControlContainer>
          <Can I="delete" a="Company">
            {() => (
              <Control
                active={hasCheckedCompanies}
                disabled={!hasCheckedCompanies}
                onClick={() => {
                  setModal('single.deleteCompanies', true);
                }}
              >
                <SVG.Icon.Trash
                  color={hasCheckedCompanies ? '#364856' : '#a7a7a7'}
                  width={24}
                  height={24}
                />
              </Control>
            )}
          </Can>

          <Control onClick={clearAll}>
            <SVG.Icon.SVGIconGrayBroom color="#364856" width={24} height={24} />
          </Control>
          <Control
            onClick={() => {
              setModal('single.filterCompany', true);
            }}
          >
            <SVG.Icon.Filter width={24} height={24} borderOnly={!hasFilters} />
          </Control>
        </ControlContainer>
      </>
    ) : null;
  };

  const total = getInvoice('filters.companies.data.meta.total');
  const currentPage = searchFilter.page ?? 1;
  const itemsPerPage = searchFilter.limit ?? 0;
  const setPage = (page: number) =>
    setSearchFilter({
      ...searchFilter,
      page,
    });

  const setItemsPerPage = (limit: number) =>
    setSearchFilter({
      ...searchFilter,
      page: 1,
      limit,
    });
  const TableControl = () => {
    return (
      <Table.Control
        currentPage={currentPage}
        itemsPerPage={itemsPerPage}
        totalOfItems={total}
        setPage={setPage}
        setItemsPerPage={setItemsPerPage}
      />
    );
  };
  return {
    companies,
    checkCompanyIds,
    setCheckCompanyIds,
    allCompanyChecks,
    companyCheckAll,
    checkCompanyId,
    deleteCompanies,
    deleteCompanyLoadings,
    CompanyControls,
    editCompany,
    searchFilter,
    setSearchFilter,
    getFilterField,
    setFilterField,
    getSortField,
    setSortField,
    getAutocompleteItems,
    setAutocompleteItems,
    setAutocompleteFilterField,
    setField,
    onLineClickHandler,
    filterCompany,
    setInvoice,
    TableControl,
    getFormMessage,
    createCompany,
    updateCompany,
    getInvoice,
    setModal,
    validateForm,
  };
};

export default useCompaniesController;
